/**
 * Structural delta between Accepted First Survey and Completed Final Survey.
 *
 * classification:
 * - null = legacy / not evaluated
 * - pending_review = delta generated, awaiting staff Yes/No
 * - no_reapproval = staff chose No
 * - customer_reapproval_required = staff chose Yes
 */

import {
  buildSurveyDeltaReview,
  flattenVisualDesignerFieldsForSnapshot,
  type SurveyDeltaReviewSection,
} from './surveyDeltaReview';

/** null = legacy/not evaluated */
export type SurveyDeltaClassification =
  | null
  | 'pending_review'
  | 'no_reapproval'
  | 'customer_reapproval_required';

/** Staff-facing Final lifecycle after Mark Complete (derived from latest delta). */
export type FinalSurveyOperationalState =
  | 'in_progress'
  | 'pending_review'
  | 'operational'
  | 'awaiting_customer_reapproval'
  | 'awaiting_customer_acceptance';

export function finalSurveyOperationalState(args: {
  completionStatus: 'in_progress' | 'complete' | string | null | undefined;
  latestClassification?: SurveyDeltaClassification;
  customerAcceptedAt?: string | null;
  reapprovalTransmissionVersionId?: string | null;
}): FinalSurveyOperationalState {
  const complete = String(args.completionStatus || '').toLowerCase() === 'complete';
  if (!complete) return 'in_progress';
  if (args.customerAcceptedAt) return 'operational';
  const c = args.latestClassification;
  if (c === 'no_reapproval') return 'operational';
  if (c === 'customer_reapproval_required') {
    return args.reapprovalTransmissionVersionId
      ? 'awaiting_customer_acceptance'
      : 'awaiting_customer_reapproval';
  }
  // pending_review, null (legacy complete), or missing decision → not operational yet
  return 'pending_review';
}

export function isFinalSurveyOperational(args: {
  completionStatus: 'in_progress' | 'complete' | string | null | undefined;
  latestClassification?: SurveyDeltaClassification;
  customerAcceptedAt?: string | null;
  reapprovalTransmissionVersionId?: string | null;
}): boolean {
  return finalSurveyOperationalState(args) === 'operational';
}

export type SurveyDeltaLineSummary = {
  name: string;
  lineKind: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  isVisualDesigner: boolean;
  widthMm: number | null;
  heightMm: number | null;
  materialScopeKey: string;
  /** Human-readable comparable fields for Review Changes (optional on legacy snapshots). */
  fields?: Record<string, string>;
};

export type SurveyDeltaComparedAgainst = 'first_survey' | 'previous_final';

export function resolveFinalSurveyComparisonBaseline(_previousDelta?: {
  id?: string | null;
  finalVersionId?: string | null;
} | null): {
  comparedAgainst: SurveyDeltaComparedAgainst;
  baselineVersionId: string | null;
  previousDeltaId: string | null;
} {
  return {
    comparedAgainst: 'first_survey',
    baselineVersionId: null,
    previousDeltaId: null,
  };
}

export function finalSurveyReviewIntro(comparedAgainst?: SurveyDeltaComparedAgainst | null): string {
  if (comparedAgainst === 'previous_final') {
    return 'The Final Survey is complete. Review the changes made since the previous Final Survey, then decide whether they need to approve the updated details.';
  }
  return 'The Final Survey is complete. Review the changes made since the original quote was sent to the customer, then decide whether they need to approve the updated details.';
}

export type SurveyDeltaSnapshot = {
  schemaVersion: 1;
  comparedAt: string;
  /** What the Original column is compared against. Defaults to first_survey for legacy snapshots. */
  comparedAgainst?: SurveyDeltaComparedAgainst;
  previousDeltaId?: string | null;
  first: {
    surveyId: string;
    versionId: string;
    header: Record<string, unknown>;
    lines: SurveyDeltaLineSummary[];
  };
  final: {
    surveyId: string;
    versionId: string;
    header: Record<string, unknown>;
    lines: SurveyDeltaLineSummary[];
  };
  changes: {
    headerFieldDiffs: string[];
    commercialLineCountChanged: boolean;
    visualDimensionalChanged: boolean;
    materialScopeOrPricingChanged: boolean;
    notes: string[];
    review?: SurveyDeltaReviewSection[];
  };
  classification: SurveyDeltaClassification;
};
function asNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function lineSummary(row: any): SurveyDeltaLineSummary {
  if (row && typeof row === 'object' && typeof row.isVisualDesigner === 'boolean' && row.materialScopeKey != null) {
    return {
      name: String(row.name || ''),
      lineKind: String(row.lineKind || row.line_kind || 'other'),
      qty: Number(row.qty || 0),
      unitPrice: Number(row.unitPrice ?? row.unit_price ?? 0),
      lineTotal: Number(row.lineTotal ?? row.line_total ?? 0),
      isVisualDesigner: !!row.isVisualDesigner,
      widthMm: asNumber(row.widthMm),
      heightMm: asNumber(row.heightMm),
      materialScopeKey: String(row.materialScopeKey || ''),
      fields: row.fields && typeof row.fields === 'object' ? row.fields : undefined,
    };
  }
  const meta = row?.meta_json ?? row?.metaJson ?? {};
  const specs = meta?.specs && typeof meta.specs === 'object' ? meta.specs : {};
  const isVd = String(meta?.feature || '').trim() === 'visual_designer';
  const name = String(row?.name || '');
  const lineKind = String(row?.line_kind || row?.lineKind || 'other');
  const qty = Number(row?.qty || 0);
  const unitPrice = Number(row?.unit_price ?? row?.unitPrice ?? 0);
  const lineTotal = Number(row?.line_total ?? row?.lineTotal ?? qty * unitPrice);
  return {
    name,
    lineKind,
    qty,
    unitPrice,
    lineTotal,
    isVisualDesigner: isVd,
    widthMm: asNumber(specs.width_mm),
    heightMm: asNumber(specs.height_mm),
    materialScopeKey: isVd
      ? `vd:${name}:${asNumber(specs.width_mm) ?? ''}:${asNumber(specs.height_mm) ?? ''}`
      : `${lineKind}:${name}:${qty}:${unitPrice}:${lineTotal}`,
    fields: isVd ? flattenVisualDesignerFieldsForSnapshot(row) : undefined,
  };
}

function headerSnapshot(header: any): Record<string, unknown> {
  return {
    title: header?.title ?? null,
    description: header?.description ?? null,
    admin_pct: header?.admin_pct ?? null,
    admin_cap: header?.admin_cap ?? null,
    contingency_pct: header?.contingency_pct ?? null,
    profit_pct: header?.profit_pct ?? null,
    vat_enabled: header?.vat_enabled ?? null,
    vat_pct: header?.vat_pct ?? null,
    has_deposit: header?.has_deposit ?? null,
    deposit_amount: header?.deposit_amount ?? null,
    deposit_percentage: header?.deposit_percentage ?? null,
    project_days: header?.project_days ?? null,
    project_hours: header?.project_hours ?? null,
    payment_structure_applicable: header?.payment_structure_applicable ?? null,
    payment_structure_pattern: header?.payment_structure_pattern ?? null,
  };
}

const HEADER_COMPARE_KEYS = [
  'title',
  'description',
  'admin_pct',
  'admin_cap',
  'contingency_pct',
  'profit_pct',
  'vat_enabled',
  'vat_pct',
  'has_deposit',
  'deposit_amount',
  'deposit_percentage',
  'project_days',
  'project_hours',
  'payment_structure_applicable',
  'payment_structure_pattern',
] as const;

export function buildSurveyDeltaSnapshot(input: {
  firstSurveyId: string;
  finalSurveyId: string;
  firstVersionId: string;
  finalVersionId: string;
  firstHeader: any;
  finalHeader: any;
  firstLines: any[];
  finalLines: any[];
  comparedAt?: string;
  classification?: SurveyDeltaClassification;
  comparedAgainst?: SurveyDeltaComparedAgainst;
  previousDeltaId?: string | null;
}): SurveyDeltaSnapshot {
  const firstLines = (input.firstLines || []).map(lineSummary);
  const finalLines = (input.finalLines || []).map(lineSummary);
  const firstHeader = headerSnapshot(input.firstHeader);
  const finalHeader = headerSnapshot(input.finalHeader);

  const headerFieldDiffs = HEADER_COMPARE_KEYS.filter((key) => {
    return JSON.stringify(firstHeader[key]) !== JSON.stringify(finalHeader[key]);
  }) as string[];

  const firstCommercial = firstLines.filter((l) => !l.isVisualDesigner);
  const finalCommercial = finalLines.filter((l) => !l.isVisualDesigner);
  const firstVd = firstLines.filter((l) => l.isVisualDesigner);
  const finalVd = finalLines.filter((l) => l.isVisualDesigner);

  const commercialKey = (lines: SurveyDeltaLineSummary[]) =>
    lines.map((l) => l.materialScopeKey).sort().join('|');
  const commercialLineCountChanged = firstCommercial.length !== finalCommercial.length
    || commercialKey(firstCommercial) !== commercialKey(finalCommercial);

  const vdDimKey = (lines: SurveyDeltaLineSummary[]) =>
    lines.map((l) => `${l.name}:${JSON.stringify(l.fields || { width: l.widthMm, height: l.heightMm })}`).sort().join('|');
  const visualDimensionalChanged = vdDimKey(firstVd) !== vdDimKey(finalVd);

  const materialScopeOrPricingChanged = commercialLineCountChanged
    || headerFieldDiffs.some((k) => ['admin_pct', 'admin_cap', 'contingency_pct', 'profit_pct', 'vat_enabled', 'vat_pct', 'has_deposit', 'deposit_amount', 'deposit_percentage'].includes(k));

  const notes: string[] = [];
  if (headerFieldDiffs.length) notes.push(`Header fields changed: ${headerFieldDiffs.join(', ')}`);
  if (visualDimensionalChanged) notes.push('Visual/dimensional fields changed');
  if (materialScopeOrPricingChanged) notes.push('Material scope, specification, or pricing changed');
  if (!notes.length) notes.push('No structural differences detected');

  return {
    schemaVersion: 1,
    comparedAt: input.comparedAt || new Date().toISOString(),
    comparedAgainst: input.comparedAgainst || 'first_survey',
    previousDeltaId: input.previousDeltaId || null,
    first: {
      surveyId: input.firstSurveyId,
      versionId: input.firstVersionId,
      header: firstHeader,
      lines: firstLines,
    },
    final: {
      surveyId: input.finalSurveyId,
      versionId: input.finalVersionId,
      header: finalHeader,
      lines: finalLines,
    },
    changes: {
      headerFieldDiffs,
      commercialLineCountChanged,
      visualDimensionalChanged,
      materialScopeOrPricingChanged,
      notes,
      review: buildSurveyDeltaReview({
        firstHeader,
        finalHeader,
        firstLines,
        finalLines,
      }),
    },
    classification: input.classification === undefined ? null : input.classification,
  };
}
